import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrangeHorizontal } from "iconsax-react";
import { Avatar, Badge, Button, Dropdown, DropdownItem, ModalWrapper, Table } from "@/shared/ui";
import { formatDateDDMMYYYY } from "@/shared/utils";
import { PRIORITY_MAP, TASK_STATUSES_WITHOUT_BACKLOG, TASK_STATUS_MAP, TASK_STATUS_INDICATOR_MAP } from "../../../consts";
import { generateTaskCode } from "../../../utils";
import type { TaskListItem, TaskStatus } from "../../../types";

interface Props {
  tasks: TaskListItem[];
  projectName: string;
  onClose: () => void;
  onStatusChange: (taskId: number, status: TaskStatus) => void;
}

export default function BacklogTableModal({ tasks, projectName, onClose, onStatusChange }: Props) {
  const { t } = useTranslation("TasksPage");
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

  return (
    <ModalWrapper onClose={onClose} width="w-7xl">
      <div className="flex flex-col gap-4">
        <h4 className="text-display-2xs content-base-primary">{t("backlog.modalTitle")}</h4>

        {tasks.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-body-regular-md content-base-low">{t("backlog.emptyState")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto page-scroll pb-2 max-h-[60vh]">
            <Table.Table className="min-w-[800px]">
              <Table.Header>
                <tr>
                  <Table.HeadCell>{t("tableView.task")}</Table.HeadCell>
                  <Table.HeadCell>{t("tableView.priority")}</Table.HeadCell>
                  <Table.HeadCell>{t("tableView.assignee")}</Table.HeadCell>
                  <Table.HeadCell>{t("tableView.createdAt")}</Table.HeadCell>
                  <Table.HeadCell>{t("tableView.dueDate")}</Table.HeadCell>
                  <Table.HeadCell>{t("tableView.actions")}</Table.HeadCell>
                </tr>
              </Table.Header>

              <Table.Body>
                {tasks.map((task, index) => {
                  const priorityEntry = PRIORITY_MAP[task.priority];
                  const isLastRows = index >= tasks.length - 2;

                  return (
                    <Table.Row key={task.id}>
                      <Table.Cell isBold>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-label-xs content-action-brand">
                            {generateTaskCode(projectName, task.id)}
                          </span>
                          <span>{task.title}</span>
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <Badge
                          variant="soft"
                          color={priorityEntry.color}
                          text={t(priorityEntry.label)}
                          dot
                        />
                      </Table.Cell>

                      <Table.Cell>
                        {task.assigned_to_name ? (
                          <div className="flex items-center gap-2">
                            <Avatar name={task.assigned_to_name} size={28} textClassName="text-body-bold-xs" />
                            <span>{task.assigned_to_name}</span>
                          </div>
                        ) : (
                          <span className="content-base-low">{t("tableView.unassigned")}</span>
                        )}
                      </Table.Cell>

                      <Table.Cell>{formatDateDDMMYYYY(task.created_at)}</Table.Cell>

                      <Table.Cell>
                        {task.due_date ? formatDateDDMMYYYY(task.due_date) : t("tableView.noDueDate")}
                      </Table.Cell>

                      <Table.Cell>
                        <Dropdown
                          open={openDropdownId === task.id}
                          onClose={() => setOpenDropdownId(null)}
                          direction={isLastRows ? "top" : "bottom"}
                          align="right"
                          width="w-max">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setOpenDropdownId(openDropdownId === task.id ? null : task.id)}>
                            <ArrangeHorizontal size={16} color="currentColor" />
                            {t("backlog.moveButton")}
                          </Button>
                          <div className="p-1">
                            {TASK_STATUSES_WITHOUT_BACKLOG.map((status) => (
                              <DropdownItem
                                key={status}
                                onClick={() => {
                                  onStatusChange(task.id, status);
                                  setOpenDropdownId(null);
                                }}>
                                <span className={`w-2 h-2 rounded-full shrink-0 ${TASK_STATUS_INDICATOR_MAP[status].dot}`} />
                                {t(TASK_STATUS_MAP[status].label)}
                              </DropdownItem>
                            ))}
                          </div>
                        </Dropdown>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Table>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
}
